/**
 * Inbox channel registry — single source for tab labels, icons, and connection mapping.
 * Add new channels here; tabs appear when configured or when conversations exist.
 */
import {
  Inbox, MessageCircle, Mail, Linkedin, Send, MessageSquare, Globe, HelpCircle,
  Facebook, Instagram, Hash,
} from "lucide-react";

/** @typedef {{ id: string, label: string, icon: import('react').ComponentType, connectionKind: string | null, order: number }} InboxChannelDef */

/** @type {InboxChannelDef[]} */
export const INBOX_CHANNEL_REGISTRY = [
  { id: "all", label: "All", icon: Inbox, connectionKind: null, order: 0 },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, connectionKind: "whatsapp", order: 10 },
  { id: "email", label: "Gmail", icon: Mail, connectionKind: "gmail", order: 20 },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, connectionKind: "linkedin", order: 30 },
  { id: "telegram", label: "Telegram", icon: Send, connectionKind: "telegram", order: 40 },
  { id: "reddit", label: "Reddit", icon: MessageCircle, connectionKind: "reddit", order: 45 },
  { id: "quora", label: "Quora", icon: HelpCircle, connectionKind: "quora", order: 46 },
  { id: "sms", label: "SMS", icon: MessageSquare, connectionKind: "sms", order: 50 },
  { id: "slack", label: "Slack", icon: Hash, connectionKind: "slack", order: 60 },
  { id: "facebook", label: "Facebook", icon: Facebook, connectionKind: "facebook", order: 70 },
  { id: "instagram", label: "Instagram", icon: Instagram, connectionKind: "instagram", order: 80 },
  { id: "webchat", label: "Web Chat", icon: Globe, connectionKind: null, order: 90 },
];

export const INBOX_QUICK_FILTERS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "draft", label: "Draft pending" },
  { id: "open", label: "Open" },
  { id: "closed", label: "Closed" },
];

const QUICK_FILTER_IDS = new Set(INBOX_QUICK_FILTERS.map((f) => f.id));

/** Ignore unknown filter ids (e.g. retired resolved) until those flows ship. */
export function normalizeInboxQuickFilter(filterId) {
  const id = String(filterId || "all").toLowerCase().trim();
  return QUICK_FILTER_IDS.has(id) ? id : "all";
}

const AUTO_REPLY_CHANNELS = new Set([]);
const DRAFT_APPROVAL_CHANNELS = new Set([
  "email", "gmail", "telegram", "whatsapp", "linkedin", "linkedin_dm", "quora", "sms",
  "facebook", "instagram",
]);

/** Normalize channel id (gmail → email). */
export function normalizeInboxChannel(channel) {
  const ch = (channel || "email").toLowerCase();
  return ch === "gmail" ? "email" : ch;
}

/**
 * Channels where AI replies require manual Approve & Send (Gmail, WhatsApp, Telegram, …).
 * Pending drafts use the Thread View review panel for every messaging channel.
 */
export function channelRequiresDraftApproval(channel) {
  const ch = normalizeInboxChannel(channel);
  return DRAFT_APPROVAL_CHANNELS.has(ch) || !AUTO_REPLY_CHANNELS.has(ch);
}

/** Statuses hidden from the thread scroller (channel-aware). */
export function hiddenThreadStatuses(channel) {
  const hidden = new Set(["discarded"]);
  if (channelRequiresDraftApproval(channel)) {
    hidden.add("draft");
  }
  return hidden;
}

/**
 * Build visible tabs from server summary + configured connections.
 * @param {object} inboxSummary
 * @returns {InboxChannelDef[]}
 */
export function visibleInboxChannels(inboxSummary) {
  const stats = inboxSummary?.channels || {};
  const configured = new Set(inboxSummary?.configured_channels || []);
  const hasAny = (inboxSummary?.total || 0) > 0 || configured.size > 0;

  return INBOX_CHANNEL_REGISTRY.filter((def) => {
    if (def.id === "all") return hasAny;
    const stat = stats[def.id];
    const hasConversations = (stat?.total || 0) > 0;
    const isConfigured = def.connectionKind
      ? configured.has(def.id) || configured.has(def.connectionKind)
      : hasConversations;
    return hasConversations || isConfigured;
  }).sort((a, b) => a.order - b.order);
}

/**
 * @param {string} channelId
 * @param {object} inboxSummary
 */
export function channelTabCounts(channelId, inboxSummary) {
  if (!inboxSummary) return { total: 0, unread: 0 };
  if (channelId === "all") {
    return { total: inboxSummary.total || 0, unread: inboxSummary.unread || 0 };
  }
  const s = inboxSummary.channels?.[channelId] || {};
  return { total: s.total || 0, unread: s.unread || 0 };
}

export function channelDef(channelId) {
  return INBOX_CHANNEL_REGISTRY.find((c) => c.id === channelId) || INBOX_CHANNEL_REGISTRY[0];
}

/**
 * True when the channel has inbox history but is not currently configured/connected.
 * @param {string} channelId
 * @param {object} inboxSummary
 */
export function isChannelDisconnected(channelId, inboxSummary) {
  if (!channelId || channelId === "all") return false;
  const def = channelDef(channelId);
  const configured = new Set(inboxSummary?.configured_channels || []);
  const isConfigured = def?.connectionKind
    ? configured.has(channelId) || configured.has(def.connectionKind)
    : configured.has(channelId);
  if (isConfigured) return false;
  const health = inboxSummary?.channel_health?.[channelId];
  if (health?.health === "disconnected") return true;
  const total = inboxSummary?.channels?.[channelId]?.total || 0;
  return total > 0;
}

/**
 * Tooltip / accessible description for a channel tab.
 * @param {string} channelId
 * @param {object} inboxSummary
 */
export function channelTabTitle(channelId, inboxSummary) {
  if (!isChannelDisconnected(channelId, inboxSummary)) return undefined;
  const health = inboxSummary?.channel_health?.[channelId];
  return health?.hint || (
    "Channel disconnected. Historical conversations are still available. "
    + "Reconnect the channel to receive new messages."
  );
}
